import { useStepStatusSync } from '@/store/useStepStatusSync'
// apps/web/src/pages/account/steps/Aid.tsx

import StepHeader from '@/components/account/StepHeader'
// import { useStepCompletionSync } from '@/hooks/useStepCompletionSync';
import { appStore, useAppStore, isApplicationLocked } from '@/store'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { aidSchema, type Aid } from '@leangap/types/schemas/user/aid/aid'
import { formatBytes } from '@/utils/file'
import { uploadAidFile, deleteAidFile } from '@/services/file'

import React, { useState, useEffect } from 'react'
import { Button, Checkbox, Input, Progress, Select, SelectItem } from '@heroui/react'
import { Upload, Trash2, FileText, AlertCircle } from 'lucide-react'
import PdfFileIcon from '@/components/icons/PdfFileIcon'
import { useFormVisitValidation } from '@/hooks/useFormVisitValidation'
import { FILE_ERRORS } from '@/constants/fileErrors'

export default function Aid() {
	const { user } = useAppStore()
	if (!user) return null
	return <AidForm user={user} />
}

import { FIELD_LIMITS, type DbUser } from '@leangap/types'
import OutsideLabel from '@/components/account/OutsideLabel'

function AidForm({ user }: { user: DbUser }) {
	// Map user to form defaults (matching Aid type). Keep DB null/undefined as
	// undefined at runtime so the zod resolver treats it as missing and returns
	// the validation error from the schema. We cast the defaults to Aid to
	// satisfy TypeScript while keeping runtime behavior.
	const toDefaults = (u: DbUser) => ({
		is_required_aid: u.is_required_aid == null ? undefined : Boolean(u.is_required_aid),
		amount: u.aid_amount != null ? String(u.aid_amount) : '',
		// Default to 'USD' if aid is required and no currency is set or it's empty string.
		// This prevents validation failures when user selects Yes but hasn't explicitly chosen currency.
		currency:
			u.aid_currency && u.aid_currency.trim() !== ''
				? u.aid_currency
				: u.is_required_aid
					? 'USD'
					: '',
		file_id: u.aid_file_id ?? null,
		is_terms_accepted: u.aid_is_terms_accepted ?? false,
	})

	const form = useForm<Aid>({
		resolver: zodResolver(aidSchema),
		// Cast to Aid because default may contain `undefined` for
		// is_required_aid at runtime; resolver will handle validation.
		defaultValues: toDefaults(user) as unknown as Aid,
		mode: 'onChange',
		criteriaMode: 'all',
	})

	const {
		setValue,
		watch,
		getValues,
		formState: { errors, isValid },
		reset,
	} = form

	// Helper to decide whether to show validation UI for a specific field.
	// We only show errors immediately if the step was previously visited, the
	// form has been submitted, or the specific field has been touched. This
	// prevents newly-revealed fields (when selecting "Yes") from appearing in
	// error state on first view.
	const shouldShowErrorFor = (field: keyof Aid) => {
		const touched = (form.formState.touchedFields as unknown as Record<string, unknown>) || {}
		return !!(form.formState.isSubmitted || visited || touched[field])
	}

	const visited = useFormVisitValidation('aid', form)

	const requiresAid = watch('is_required_aid')
	// Track whether the select has been interacted with locally so we don't
	// immediately override the DB-derived empty display. This helps show the
	// placeholder until the user touches the control.
	const [selectTouched, setSelectTouched] = useState(false)

	// Compute the select key to show: if the user hasn't interacted with the form
	// yet (not dirty) and the DB value is null/undefined, show the blank option.
	// Once the user touches the control (form becomes dirty) prefer the form value.
	// Decide which value drives the Select display:
	// - If the user hasn't touched the select yet, prefer the DB value so a
	//   null DB value shows the blank option.
	// - Once the select is touched, prefer the form's current value (which may
	//   be undefined if the user selected the blank option).
	const selectedKey = !selectTouched
		? user.is_required_aid === true
			? 'yes'
			: user.is_required_aid === false
				? 'no'
				: ''
		: requiresAid === true
			? 'yes'
			: requiresAid === false
				? 'no'
				: ''
	const amount = watch('amount')
	const currency = watch('currency')
	const accepted = watch('is_terms_accepted')
	const fileId = watch('file_id')

	// Helper: handle the side-effects when the user explicitly selects "No"
	// or when the user clears the selection but there is existing aid data to
	// remove. `markComplete` controls whether application_status.aid should
	// be set to true (explicit No) or false (empty placeholder clearing).
	// When markComplete=false (clearing a previously entered "Yes" with the
	// empty placeholder), we avoid forcing the form value to false so the
	// UI remains in the "unspecified" state; when markComplete=true (explicit
	// "No"), we ensure the form and store reflect false.
	const processNoSelection = async (markComplete = true) => {
		const currentUser = appStore.snapshot().user
		const hasFile = Boolean(
			currentUser && (currentUser.aid_file_id || currentUser.file_path || currentUser.file_original)
		)
		if (hasFile) {
			appStore.setFileOperation(true)
			try {
				await clearFile()
				const backendUser = appStore.snapshot().user
				if (backendUser) syncWithBackend(backendUser)
				// Ensure form values reflect NO selection only for explicit "No".
				if (markComplete) {
					setValue('is_required_aid', false, { shouldValidate: true, shouldDirty: true })
				}
				setValue('amount', '', { shouldValidate: true, shouldDirty: true })
				setValue('currency', '', { shouldValidate: true, shouldDirty: true })
				setValue('is_terms_accepted', false, { shouldValidate: true, shouldDirty: true })
				setFileMeta(null)
				setValue('file_id', null, { shouldValidate: true, shouldDirty: true })
				// Also clear aid fields in the store and persist so DB/HubSpot reflect the cleared selection.
				// Persist application_status.aid according to markComplete (true for explicit No, false for clearing).
				const cur = appStore.snapshot().user?.application_status ?? {
					general: false,
					background: false,
					program: false,
					aid: false,
				}
				appStore.patch({
					is_required_aid: markComplete ? false : null,
					aid_amount: null,
					aid_currency: null,
					aid_is_terms_accepted: null,
					application_status: { ...cur, aid: markComplete },
				})
				await appStore.save()
				const backendUserAfter = appStore.snapshot().user
				if (backendUserAfter) syncWithBackend(backendUserAfter)
			} catch (err) {
				setFileError(err instanceof Error ? err.message : FILE_ERRORS.DELETE_EVIDENCE_FAILED)
			} finally {
				appStore.setFileOperation(false)
			}
		} else {
			// No remote file exists — just clear local form values and persist
			appStore.setFileOperation(true)
			try {
				setValue('amount', '', { shouldValidate: true, shouldDirty: true })
				setValue('currency', '', { shouldValidate: true, shouldDirty: true })
				setValue('is_terms_accepted', false, { shouldValidate: true, shouldDirty: true })
				setFileMeta(null)
				setValue('file_id', null, { shouldValidate: true, shouldDirty: true })
				const cur = appStore.snapshot().user?.application_status ?? {
					general: false,
					background: false,
					program: false,
					aid: false,
				}
				appStore.patch({
					is_required_aid: markComplete ? false : null,
					aid_amount: null,
					aid_currency: null,
					aid_is_terms_accepted: null,
					aid_file_id: null,
					file_path: null,
					file_original: null,
					file_mime: null,
					file_size: null,
					application_status: { ...cur, aid: markComplete },
				})
				// Failure feedback is shown by appStore.save; edits stay dirty for retry.
				await appStore.save().catch(() => {})
				const backendUser = appStore.snapshot().user
				if (backendUser) syncWithBackend(backendUser)
			} finally {
				appStore.setFileOperation(false)
			}
		}
	}

	// Selection change handler moved out of JSX to keep render clean
	const handleAidSelectionChange = (keys: string | { currentKey?: string } | string[]) => {
		setSelectTouched(true)
		const key = Array.isArray(keys)
			? keys[0]
			: typeof keys === 'string'
				? keys
				: ((keys as unknown as { currentKey?: string })?.currentKey ?? '')
		const k = key as 'yes' | 'no' | '' | undefined
		const next = k === 'yes'
		// If the empty option was selected, set the form value to `undefined` so
		// the zod resolver treats it as missing and returns the validation error
		// from the schema. If a valid choice is selected, clear any previous errors.
		if (k === '' || k === undefined) {
			setValue('is_required_aid', undefined as unknown as boolean, {
				shouldValidate: true,
				shouldDirty: true,
			})

			// If the user cleared the selection but there is existing aid data
			// (amount, file, or accepted terms), clear those values so we don't
			// leave stale data when the step is logically incomplete.
			const snapshot = appStore.snapshot().user
			const curVals = getValues()
			const hasExistingAidData = Boolean(
				(snapshot &&
					(snapshot.aid_amount || snapshot.aid_file_id || snapshot.aid_is_terms_accepted)) ||
					curVals.amount ||
					curVals.currency ||
					curVals.is_terms_accepted ||
					curVals.file_id
			)
			if (hasExistingAidData) {
				void processNoSelection(false)
			}
		} else {
			setValue('is_required_aid', next, { shouldValidate: true, shouldDirty: true })
			if (form.formState.errors.is_required_aid) form.clearErrors('is_required_aid')
		}

		// Do not trigger validation or patch the store here. The watch
		// subscription will pick up the form changes and persist them. This
		// keeps behavior consistent with other steps (see General.tsx).
		// When user explicitly selects 'No', clear remote/local file info and
		// persist the cleared state. Do NOT treat the empty placeholder the
		// same as 'No' — an empty selection represents an unspecified choice
		// and should leave existing evidence intact while showing the step as
		// incomplete.
		if (k === 'no') {
			// For explicit 'no', only perform the heavy cleanup if there's existing
			// aid data (file/amount/terms). Otherwise, do a lightweight clear and
			// persist application_status.aid = true without file ops.
			const snapshot = appStore.snapshot().user
			const curVals = getValues()
			const hasExistingAidData = Boolean(
				(snapshot &&
					(snapshot.aid_amount || snapshot.aid_file_id || snapshot.aid_is_terms_accepted)) ||
					curVals.amount ||
					curVals.currency ||
					curVals.is_terms_accepted ||
					curVals.file_id
			)
			if (hasExistingAidData) {
				void processNoSelection(true)
			} else {
				// No remote/local data to clean: just set form values and persist
				setValue('is_required_aid', false, { shouldValidate: true, shouldDirty: true })
				setValue('amount', '', { shouldValidate: true, shouldDirty: true })
				setValue('currency', '', { shouldValidate: true, shouldDirty: true })
				setValue('is_terms_accepted', false, { shouldValidate: true, shouldDirty: true })
				setFileMeta(null)
				setValue('file_id', null, { shouldValidate: true, shouldDirty: true })
				const cur = appStore.snapshot().user?.application_status ?? {
					general: false,
					background: false,
					program: false,
					aid: false,
				}
				appStore.patch({
					is_required_aid: false,
					aid_amount: null,
					aid_currency: null,
					aid_is_terms_accepted: null,
					application_status: { ...cur, aid: true },
				})
				void appStore.save().catch(() => {})
			}
		} else {
			// Ensure a default currency is set immediately so the watch-driven
			// autosave includes it (prevents backend from reverting a Yes
			// selection due to missing persisted currency).
			if (!currency) setValue('currency', 'USD', { shouldValidate: true, shouldDirty: true })
		}
	}

	// File meta for UI (not in schema)
	const [uploading, setUploading] = useState(false)
	const [deleting, setDeleting] = useState(false)
	const [fileError, setFileError] = useState<string | null>(null)
	const [fileMeta, setFileMeta] = useState<{ name: string; size: number; mime: string } | null>(
		user.file_original && user.file_size
			? {
					name: user.file_original,
					size: user.file_size,
					mime: user.file_mime ?? 'application/pdf',
				}
			: null
	)

	// Whether the application is locked (Submitted, Admitted, or Not Accepted)
	const isSubmitted = isApplicationLocked(user.application_process)

	// Only patch the store if the user is actively editing (not after backend sync)
	const [isSyncing, setIsSyncing] = useState(false)
	// Read global fileOperation flag from store to block UI across the app during file operations
	const { fileOperation } = useAppStore()
	const isBusy = uploading || deleting || Boolean(fileOperation)

	// Update step status based on current form validity and busy/sync state.
	useStepStatusSync(
		'aid',
		isValid && !isBusy && !isSyncing,
		form.formState.isDirty,
		form.formState.isSubmitted
	)
	useEffect(() => {
		if (isSubmitted) return
		if (isSyncing) return
		const sub = watch((v) => {
			// Only autosave/patch when the user has interacted (dirty/submitted)
			// or when the user previously visited this form (visited flag returned by hook).
			const shouldPatch = form.formState.isDirty || form.formState.isSubmitted || visited
			if (!shouldPatch) return

			// Build patch defensively: only persist the "Yes" related fields when
			// the form is valid. This avoids saving a partial "Yes" (e.g. empty
			// amount/unchecked terms) that the backend may reject and then revert
			// the user to No.
			const patch: Record<string, unknown> = {
				is_required_aid: v.is_required_aid === undefined ? null : v.is_required_aid ? true : false,
				// Always include the amount field (can be null) so UI reflects current entry
				aid_amount: v.amount?.trim() === '' ? null : v.amount?.trim(),
				// File metadata follows current UI state
				aid_file_id: v.file_id ?? null,
				file_original: fileMeta?.name ?? null,
				file_size: fileMeta?.size ?? null,
				file_mime: fileMeta?.mime ?? null,
			}

			patch.aid_currency = v.is_required_aid
				? v.currency && v.currency.trim() !== ''
					? v.currency
					: 'USD'
				: null
			patch.aid_is_terms_accepted = v.is_required_aid ? v.is_terms_accepted : null // Compute whether the Aid step should be considered complete:
			// - Explicit 'No' => complete
			// - Explicit 'Yes' AND schema-valid => complete
			// - Otherwise incomplete
			const curStatus = appStore.snapshot().user?.application_status ?? {
				general: false,
				background: false,
				program: false,
				aid: false,
			}
			let aidComplete = false
			if (v.is_required_aid === false) {
				aidComplete = true
			} else if (v.is_required_aid === true) {
				// Re-evaluate validity against the schema using the current watched values.
				const candidate = {
					is_required_aid: v.is_required_aid,
					amount: v.amount?.trim() === '' ? null : v.amount?.trim(),
					currency: v.currency && v.currency.trim() !== '' ? v.currency : 'USD',
					file_id: v.file_id ?? null,
					is_terms_accepted: v.is_terms_accepted ?? null,
				}
				const schemaValid = aidSchema.safeParse(candidate).success
				// For the upload path, allow marking the step complete based on
				// schema validation alone; `isBusy` is true during the file
				// operation but we still want the application_status to reflect
				// the user's completed inputs once the file exists.
				aidComplete = schemaValid && !isSyncing
			} else {
				aidComplete = false
			}
			patch.application_status = { ...curStatus, aid: aidComplete }

			appStore.patch(patch)
		})
		return () => sub.unsubscribe()
	}, [
		watch,
		fileMeta,
		isSyncing,
		user,
		isSubmitted,
		form.formState.isDirty,
		form.formState.isSubmitted,
		visited,
		isValid,
		isBusy,
	])

	// File upload logic (PDF only, 10MB max)
	const MAX_FILE_SIZE = 10 * 1024 * 1024
	const ALLOWED_FILE_TYPES = ['application/pdf']

	// Helper to sync store and form with backend user
	const syncWithBackend = (backendUser: DbUser) => {
		setIsSyncing(true)
		appStore.sync(backendUser)
		reset(toDefaults(backendUser))
		// After syncing with backend, the authoritative DB value should drive the
		// Select display until the user interacts again.
		setSelectTouched(false)
		// Clear any manual errors we may have set when the user selected the empty option.
		if (form.formState.errors.is_required_aid) form.clearErrors('is_required_aid')
		setFileMeta(
			backendUser.file_original && backendUser.file_size
				? {
						name: backendUser.file_original,
						size: backendUser.file_size,
						mime: backendUser.file_mime ?? 'application/pdf',
					}
				: null
		)
		setTimeout(() => setIsSyncing(false), 0) // allow effects to skip one render
	}

	const onPickFile: React.ChangeEventHandler<HTMLInputElement> = async (e) => {
		if (user.application_process === 'Submitted') return
		const f = e.currentTarget.files?.[0]
		if (!f) return
		// Clear any previous file error when user starts a new pick
		setFileError(null)
		if (!ALLOWED_FILE_TYPES.includes(f.type)) {
			setValue('file_id', null, { shouldValidate: true, shouldDirty: true })
			setFileMeta(null)
			setFileError(FILE_ERRORS.INVALID_TYPE)
			return
		}
		if (f.size > MAX_FILE_SIZE) {
			setValue('file_id', null, { shouldValidate: true, shouldDirty: true })
			setFileMeta(null)
			setFileError(FILE_ERRORS.SIZE_EXCEEDED)
			return
		}
		setUploading(true)
		appStore.setFileOperation(true)
		try {
			const data = await uploadAidFile({
				file: f,
				email: user.email,
				fullName: user.name ?? '',
				userId: user.hubspot_id ?? user._id ?? undefined, // try hubspot_id then _id
			})
			// Optimistic local update: set form value and UI meta immediately
			const path = data.path ?? null
			setValue('file_id', path, { shouldValidate: true, shouldDirty: true })
			setFileMeta({
				name: data.original ?? f.name,
				size: data.size ?? f.size,
				mime: data.mime ?? f.type ?? 'application/pdf',
			})

			// Allow previous user interactions (checkbox, amount input) to flush into
			// the form state before we read values for the store patch. This avoids
			// races in tests where the upload happens immediately after a click/type
			// and the form hasn't updated yet.
			await new Promise((res) => setTimeout(res, 0))

			// Patch the store with the file info AND current form values so the
			// subsequent save reflects the user's full intent (amount/terms/etc.).
			// This avoids races where the upload/save occurs before the watch
			// subscription has flushed form values into the store.
			const v = getValues()
			const curStatus = appStore.snapshot().user?.application_status ?? {
				general: false,
				background: false,
				program: false,
				aid: false,
			}
			let aidComplete = false
			if (v.is_required_aid === false) {
				aidComplete = true
			} else if (v.is_required_aid === true) {
				const candidate = {
					is_required_aid: v.is_required_aid,
					amount: v.amount?.trim() === '' ? null : v.amount?.trim(),
					currency: v.currency && v.currency.trim() !== '' ? v.currency : 'USD',
					file_id: path ?? null,
					is_terms_accepted: v.is_terms_accepted ?? null,
				}
				const schemaValid = aidSchema.safeParse(candidate).success
				aidComplete = schemaValid && !isSyncing && !isBusy
			} else {
				aidComplete = false
			}

			// Only patch file-related metadata here. Avoid touching aid_amount/aid_currency/
			// aid_is_terms_accepted so we don't accidentally preserve or overwrite the
			// user's intent from other flows (selection clearing, manual edits). The
			// watch subscription will persist form-only fields (amount/terms) as usual.
			appStore.patch({
				file_path: data.path ?? null,
				file_original: data.original ?? null,
				file_mime: data.mime ?? null,
				file_size: data.size ?? null,
				aid_file_id: data.path || null,
				application_status: { ...curStatus, aid: aidComplete },
			})
			// Persist to backend and update store
			await appStore.save()
			// Sync store and form with backend user
			const backendUser = appStore.snapshot().user
			if (backendUser) syncWithBackend(backendUser)
		} catch (err) {
			setValue('file_id', null, { shouldValidate: true })
			setFileMeta(null)
			// Also clear from store on error
			appStore.patch({
				file_path: null,
				file_original: null,
				file_mime: null,
				file_size: null,
				aid_file_id: null,
			})
			setFileError(err instanceof Error ? err.message : FILE_ERRORS.UPLOAD_FAILED)
		} finally {
			setUploading(false)
			appStore.setFileOperation(false)
		}
	}

	const clearFile = async () => {
		if (user.application_process === 'Submitted') return
		if (isBusy) return // prevent re-entrant delete / disable while processing
		// Determine whether there's a remote file to delete. Prefer the form fileId, then store user fields, then local fileMeta.
		const currentUser = appStore.snapshot().user
		const remoteExists = Boolean(
			fileId ||
				(currentUser &&
					(currentUser.aid_file_id || currentUser.file_path || currentUser.file_original)) ||
				fileMeta
		)
		if (!remoteExists) {
			// Nothing to delete remotely — just clear local UI/form
			setFileMeta(null)
			setValue('file_id', null, { shouldValidate: true })
			return
		}
		setDeleting(true)
		appStore.setFileOperation(true)
		try {
			// Call server deletion endpoint. Server should remove the file from Backblaze, HubSpot, and update DB.
			const snapshot = appStore.snapshot()
			const effectiveFileId =
				fileId ?? (snapshot.user && (snapshot.user.aid_file_id || snapshot.user.file_path)) ?? null
			await deleteAidFile({ email: user.email, file_id: effectiveFileId ?? undefined })
			// Backend returns metadata about the deletion; regardless, clear all local file metadata and form state
			appStore.patch({
				file_path: null,
				file_original: null,
				file_mime: null,
				file_size: null,
				aid_file_id: null,
			})
			// Immediately update the form/UI to reflect deletion
			setFileMeta(null)
			setValue('file_id', null, { shouldValidate: true, shouldDirty: true })
			// Persist to backend and update store
			await appStore.save()
			// Defensive: ensure backend didn't leave any stray file metadata. If it did, clear again and save.
			const afterSave = appStore.snapshot().user
			if (
				afterSave &&
				(afterSave.file_path ||
					afterSave.file_original ||
					afterSave.file_mime ||
					afterSave.file_size ||
					afterSave.aid_file_id)
			) {
				appStore.patch({
					file_path: null,
					file_original: null,
					file_mime: null,
					file_size: null,
					aid_file_id: null,
				})
				await appStore.save()
			}
			// Sync store and form with backend user (final authoritative state)
			const backendUser = appStore.snapshot().user
			if (backendUser) syncWithBackend(backendUser)
		} catch (err) {
			// On error, ensure we didn't leave stale local form state if delete failed
			setFileMeta(null)
			setValue('file_id', null, { shouldValidate: true, shouldDirty: true })
			appStore.patch({
				file_path: null,
				file_original: null,
				file_mime: null,
				file_size: null,
				aid_file_id: null,
			})
			await appStore.save().catch(() => {})
			setFileError(err instanceof Error ? err.message : FILE_ERRORS.DELETE_FAILED)
		} finally {
			setDeleting(false)
			appStore.setFileOperation(false)
		}
	}

	// Show danger style on the checkbox only when Aid = Yes and terms are not accepted
	// Special-case the terms checkbox: only show the danger state if the checkbox
	// itself was touched or the form was submitted. We intentionally exclude the
	// `visited` heuristic here so the checkbox doesn't appear red immediately when
	// the user selects "Yes" and the field is first revealed.
	const touchedFields = (form.formState.touchedFields as unknown as Record<string, unknown>) || {}
	const showTermsDanger =
		requiresAid &&
		!accepted &&
		(form.formState.isSubmitted || Boolean(touchedFields['is_terms_accepted']))

	return (
		<form className="space-y-8 pb-12">
			<StepHeader
				title="Financial Aid"
				lead="Complete this section if you want to apply for financial aid"
			/>

			<p>
				Financial aid is <span className="underline">need-based</span>. Scholarships are provided if
				you demonstrate you can't pay in full due to your financial situation. You are only eligible
				to apply for financial need support if your family's income in the last fiscal year was
				below $100,000 USD. This will be verified using tax returns.
			</p>

			{/* Scholarship cards */}
			<div className="grid gap-4 sm:grid-cols-2">
				<div className="bg-content1 rounded-xl p-4">
					<h3 className="mb-2 font-medium">Leangap Online</h3>
					<p className="text-default-400 text-sm">Scholarships range from</p>
					<p className="text-success text-2xl font-bold">10% – 80%</p>
				</div>
				<div className="bg-content1 rounded-xl p-4">
					<h3 className="mb-2 font-medium">Leangap San Francisco</h3>
					<p className="text-default-400 text-sm">Scholarships range from</p>
					<p className="text-success text-2xl font-bold">10% – 50%</p>
				</div>
			</div>

			{/* Warning notice */}
			<div className="flex items-center gap-3 rounded-xl bg-orange-500/10 p-4">
				<AlertCircle className="text-warning size-5 shrink-0" />
				<p className="text-warning text-sm font-semibold">
					Unfortunately, we do not provide full scholarships, without exceptions.
				</p>
			</div>

			<div className="grid grid-cols-1 items-start gap-12 md:grid-cols-6 md:gap-4">
				<Select
					className="col-span-6 md:col-span-2"
					label={<OutsideLabel>Do you require financial aid?</OutsideLabel>}
					labelPlacement="outside"
					isRequired
					size="lg"
					isDisabled={isBusy || isSubmitted}
					isInvalid={!!errors.is_required_aid}
					errorMessage={errors.is_required_aid?.message}
					// Show empty default when DB value is null/undefined. Use form value when available.
					selectedKeys={[selectedKey]}
					onSelectionChange={handleAidSelectionChange}
				>
					<SelectItem key="">Select</SelectItem>
					<SelectItem key="yes">Yes</SelectItem>
					<SelectItem key="no">No</SelectItem>
				</Select>

				<Input
					className="col-span-6 md:col-span-4"
					size="lg"
					label={<OutsideLabel>Total family income (in USD) in the last fiscal year</OutsideLabel>}
					labelPlacement="outside"
					placeholder="Amount in USD"
					inputMode="numeric"
					type="text"
					pattern="[0-9]*"
					maxLength={FIELD_LIMITS.aidAmount}
					autoComplete="off"
					value={amount ?? ''}
					onValueChange={(val: string) =>
						setValue('amount', val.replace(/\D/g, ''), {
							shouldValidate: true,
							shouldDirty: true,
						})
					}
					isDisabled={!requiresAid || isSubmitted}
					isReadOnly={isSubmitted}
					isInvalid={!!errors.amount && shouldShowErrorFor('amount')}
					errorMessage={
						errors.amount && shouldShowErrorFor('amount') ? errors.amount?.message : undefined
					}
					isRequired={!!requiresAid}
				/>
			</div>

			{!requiresAid && <div className="bg-default-200/60 my-2 h-px" />}

			{requiresAid && (
				<>
					{/* Document requirements box */}
					<div className="bg-content1 rounded-xl p-5">
						<div className="mb-3 flex items-center gap-2">
							<FileText className="text-primary size-5" />
							<h3 className="font-medium">Required Documentation</h3>
						</div>
						<p className="mb-3 text-sm">
							Please upload one of the following from your parent or guardian as a
							<strong> single PDF</strong> (max 10MB):
						</p>
						<ul className="mb-4 ml-5 space-y-2 text-sm">
							<li className="flex items-center gap-2">
								<span className="bg-primary/20 text-primary inline-flex size-5 shrink-0 items-center justify-center rounded-full text-xs font-medium">
									1
								</span>
								Tax return from the last fiscal year
							</li>
							<li className="flex items-center gap-2">
								<span className="bg-primary/20 text-primary inline-flex size-5 shrink-0 items-center justify-center rounded-full text-xs font-medium">
									2
								</span>
								Payroll slip
							</li>
							<li className="flex items-center gap-2">
								<span className="bg-primary/20 text-primary inline-flex size-5 shrink-0 items-center justify-center rounded-full text-xs font-medium">
									3
								</span>
								Bank statements for the last 3 months
							</li>
						</ul>
						<p className="border-default-200 border-t pt-3 text-sm">
							<AlertCircle className="mr-1 mb-0.5 inline size-4" />
							If you live outside the United States, the document must be translated into English
							with amounts converted to USD. Applications that do not include valid documentation
							will not be reviewed.
						</p>
					</div>

					<section className="space-y-3">
						{!fileMeta && (
							<>
								<p className="text-sm font-medium">
									Attach evidence (PDF) <span className="text-danger text-xs">*</span>
								</p>
								<div className="space-y-2">
									<label className="inline-flex items-center gap-2">
										<input
											type="file"
											accept="application/pdf"
											className="hidden"
											onChange={onPickFile}
											disabled={isBusy || isSubmitted}
										/>
										<Button
											as="span"
											variant="solid"
											endContent={<Upload className="size-4" />}
											isDisabled={isBusy || isSubmitted}
										>
											Choose File
										</Button>
									</label>
									{uploading && <Progress aria-label="Uploading" value={70} />}
									{!uploading && errors.file_id && shouldShowErrorFor('file_id') && (
										<p className="text-danger text-sm">{errors.file_id?.message}</p>
									)}
									{fileError && <p className="text-danger text-sm">{fileError}</p>}
								</div>
							</>
						)}

						{fileMeta && (
							<div
								className="rounded-large bg-foreground-100 hover:bg-foreground-200 flex w-full flex-row items-center justify-center gap-x-4 p-2 transition-colors"
								tabIndex={0}
							>
								<div className="flex h-[56px] w-[56px] flex-shrink-0 items-center justify-center">
									<PdfFileIcon size={56} className="text-foreground-400" />
								</div>
								<div className="min-w-0 flex-1">
									<p className="truncate font-medium">{fileMeta?.name}</p>
									<p className="text-default-500 text-xs">
										{fileMeta ? formatBytes(fileMeta.size) : ''}
									</p>
								</div>
								<Button
									isIconOnly
									variant="light"
									onPress={clearFile}
									aria-label={isBusy ? 'Processing' : 'Remove file'}
									isDisabled={isBusy || isSubmitted}
								>
									{isBusy ? (
										<svg
											className="text-default-500 h-6 w-6 animate-spin"
											viewBox="0 0 24 24"
											fill="none"
											aria-hidden
										>
											<circle
												className="opacity-25"
												cx="12"
												cy="12"
												r="10"
												stroke="currentColor"
												strokeWidth="4"
											></circle>
											<path
												className="opacity-75"
												fill="currentColor"
												d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
											></path>
										</svg>
									) : (
										<Trash2 className="text-default-500 size-7" />
									)}
								</Button>
							</div>
						)}
					</section>

					<Checkbox
						className="mt-2"
						isSelected={!!accepted}
						onValueChange={(val) =>
							setValue('is_terms_accepted', val, { shouldValidate: true, shouldDirty: true })
						}
						color={showTermsDanger ? 'danger' : 'default'}
						classNames={{ label: showTermsDanger ? 'text-danger' : undefined }}
						isDisabled={isSubmitted}
					>
						I understand that I'm not guaranteed to receive financial aid.
					</Checkbox>

					{errors.is_terms_accepted && shouldShowErrorFor('is_terms_accepted') && (
						<p className="text-danger mt-1 text-sm">{errors.is_terms_accepted?.message}</p>
					)}
				</>
			)}
		</form>
	)
}
